import { Action, ActionReducer, createFeatureSelector, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import * as OrdersActions from '../actions/orders.actions';
import { IOrder, IOrderData, IOrderHistory, IPendingOrder } from '../models/order.models';
import { GlueUtils } from '../utils/glue.utils';
import { IPaginationCurrent } from '../models/settings.model';
import { OrdersUtils } from '../utils/orders.utils';
import { EGlueResource } from '../configurations/common';

export type OrderDataType = IOrder[] | IOrderData[] | undefined;

export interface IOrdersWithSapDetails {
  orders: OrderDataType;
  sapOrdersDetails: OrderDataType;
}

export interface IOrdersWithSapDetailsState {
  ordersPerPage: IOrdersWithSapDetails[],
  pagination: IPaginationCurrent,
}

export interface IOrdersHistory {
  ordersHistory: IOrderData[] | undefined;
}

export interface IOrdersCartsState {
  pendingCarts: IPendingOrder[] | undefined;
  canceledDeclinedCarts: IOrderHistory[];
  ordersHistoryPerPage: IOrdersHistory[];
  ordersHistoryPagination: IPaginationCurrent;
}

export interface ICpqOrders {
  cpqOrders: IOrderData[];
}

export interface ICpqOrdersState {
  ordersUnderReviewPerPage: ICpqOrders[];
  ordersFulfilledPerPage: ICpqOrders[];
  paginationUnderReviewOrders: IPaginationCurrent,
  paginationFulfilledOrders: IPaginationCurrent,
}

const initialPagination: IPaginationCurrent = {
  currentPage: 0,
  currentOffset: 0,
  prevPage: 0,
  prevOffset: 0,
  nextPage: 0,
  nextOffset: 0,
  firstPage: 0,
  firstOffset: 0,
  lastPage: 0,
  lastOffset: 0,
  limitPerPage: 0,
};

export const cpqOrdersBaseState: MemoizedSelector<object, ICpqOrdersState> = createFeatureSelector<ICpqOrdersState>('cpqOrders');

export const selectCpqOrdersUnderReviewPagination: MemoizedSelector<object, IPaginationCurrent> = createSelector(
  cpqOrdersBaseState,
  state => state.paginationUnderReviewOrders,
);

export const selectCpqOrdersFulfilledPagination: MemoizedSelector<object, IPaginationCurrent> = createSelector(
  cpqOrdersBaseState,
  state => state.paginationFulfilledOrders,
);

export const selectUnderReviewCpqOrdersHistoryAtPage: (pageNum: number) => MemoizedSelector<object, IOrderData[]> = (pageNum: number): MemoizedSelector<object, IOrderData[]> => {
  return createSelector(
    cpqOrdersBaseState,
    state => {
      return state.ordersUnderReviewPerPage[pageNum] ? state.ordersUnderReviewPerPage[pageNum].cpqOrders : [];
    },
  );
};

export const selectFulfilledCpqOrdersHistoryAtPage: (pageNum: number) => MemoizedSelector<object, IOrderData[]> = (pageNum: number): MemoizedSelector<object, IOrderData[]> => {
  return createSelector(
    cpqOrdersBaseState,
    state => {
      return state.ordersFulfilledPerPage[pageNum] ? state.ordersFulfilledPerPage[pageNum].cpqOrders : [];
    },
  );
};

export const ordersWithDetailsBaseState: MemoizedSelector<object, IOrdersWithSapDetailsState> = createFeatureSelector<IOrdersWithSapDetailsState>('ordersWithDetails');

export const selectLoadedSapOrders: MemoizedSelector<object, { orders: IOrder[]; sapOrdersDetails: IOrder[] }> = createSelector(
  ordersWithDetailsBaseState,
  state => {
    const loadedPages: IOrdersWithSapDetails[] = state.ordersPerPage.filter(ordersPerPage => !!ordersPerPage);
    return ({
      orders: loadedPages.flatMap(ordersPerPage => ordersPerPage.orders as IOrder[]),
      sapOrdersDetails: loadedPages.flatMap(ordersPerPage => ordersPerPage.sapOrdersDetails as IOrder[]),
    });
  },
);

export const selectSapOrdersPagination: MemoizedSelector<object, IPaginationCurrent> = createSelector(
  ordersWithDetailsBaseState,
  state => state.pagination,
);

export const selectSapOrdersAtPage: (pageNum: number) => MemoizedSelector<object, {orders: OrderDataType; sapOrdersDetails: OrderDataType}> = (pageNum: number) => {
  return createSelector(
    ordersWithDetailsBaseState,
    state => state.ordersPerPage[pageNum] ?? {orders: [], sapOrdersDetails: []},
  );
};

export const ordersCartsBaseState: MemoizedSelector<object, IOrdersCartsState> = createFeatureSelector<IOrdersCartsState>('ordersCarts');

//select pending carts from orders application store
export const selectOrdersPendingCarts: MemoizedSelector<object, IPendingOrder[] | undefined> = createSelector(
  ordersCartsBaseState,
  state => state.pendingCarts,
);

//select canceled/declined carts from orders application store
export const selectOrdersCanceledDeclinedCarts: MemoizedSelector<object, IOrderHistory[]> = createSelector(
  ordersCartsBaseState,
  state => state.canceledDeclinedCarts,
);

export interface IOrdersHistoryPage {
  currentPage: number;
  numPages: number;
  limitPerPage: number;
  orders: IOrderHistory[];
  pageLoaded: boolean;
}

/**
 * Returns selector to retrieve a page of the "mixed" orders history consisting of
 * rejected/canceled carts and sales orders
 *
 * @param {number} pageNum
 * @returns {MemoizedSelector<IOrdersCartsState, IOrdersHistoryPage>}
 */
export const selectOrdersHistoryAtPage: (pageNum: number) => MemoizedSelector<object, IOrdersHistoryPage> = (pageNum: number) => {
  return createSelector(
    ordersCartsBaseState,
    ({ordersHistoryPagination, ordersHistoryPerPage, canceledDeclinedCarts}): IOrdersHistoryPage => {
      const limitPerPage: number = ordersHistoryPagination.limitPerPage;
      if (ordersHistoryPerPage[pageNum]) {
        const ordersWithCanceledDeclinedCarts: IOrderHistory[] = mergeOrdersAndCanceledDeclinedCarts(
          ordersHistoryPerPage, canceledDeclinedCarts,
        );
        const startOffsets: number[] = recalculateMergedPageOffsets(ordersHistoryPerPage, ordersHistoryPagination);
        const startOffset: number = startOffsets[pageNum];
        const endOffset: number = startOffset + limitPerPage;

        return {
          orders: ordersWithCanceledDeclinedCarts.slice(startOffset, endOffset),
          currentPage: pageNum,
          numPages: ordersHistoryPagination.lastPage,
          pageLoaded: true,
          limitPerPage,
        };
      } else {
        return {
          orders: [],
          currentPage: pageNum,
          numPages: ordersHistoryPagination.lastPage,
          pageLoaded: false,
          limitPerPage,
        };
      }
    },
  );
};

export const initialState: IOrdersWithSapDetailsState = {
  ordersPerPage: [],
  pagination: {...initialPagination},
};

export const initialOrdersCartsState: IOrdersCartsState = {
  pendingCarts: undefined,
  canceledDeclinedCarts: [],
  ordersHistoryPerPage: [],
  ordersHistoryPagination: {...initialPagination},
};

const reducer: ActionReducer<IOrdersWithSapDetailsState> = createReducer(
  initialState,

  on(OrdersActions.SuccessGetOrdersWithSapDetailsDataAction, (state: IOrdersWithSapDetailsState, {ordersWithSapDetails}) => {
    const pagination: IPaginationCurrent = GlueUtils.parsePaginationLinks(ordersWithSapDetails.links);
    const ordersPerPage: {orders: OrderDataType; sapOrdersDetails: OrderDataType}[] = new Array(...state.ordersPerPage);
    ordersPerPage[pagination.currentPage] = {
      orders: ordersWithSapDetails?.data,
      sapOrdersDetails: ordersWithSapDetails?.data,
    };
    return {
      ...state,
      ordersPerPage,
      pagination,
    };
  }),

  on(OrdersActions.OrdersWithSapDetailsClearData, (): IOrdersWithSapDetailsState => {
    return {
      ...initialState,
    };
  }),

  on(OrdersActions.SuccessUpdateOrdersWithSapDetailsDataAction, (state: IOrdersWithSapDetailsState, {
    orders,
    sapOrdersDetails,
  }) => {
    return {
      ...state,
      orders: orders,
      sapOrdersDetails: sapOrdersDetails,
    };
  }),
);

export function OrdersReducer(state: IOrdersWithSapDetailsState | undefined, action: Action): IOrdersWithSapDetailsState {
  return reducer(state, action);
}

export const cpqOrdersInitialState: ICpqOrdersState = {
  ordersUnderReviewPerPage: [],
  ordersFulfilledPerPage: [],
  paginationFulfilledOrders: {...initialPagination},
  paginationUnderReviewOrders: {...initialPagination},
};

const cpqOrdersReducer: ActionReducer<ICpqOrdersState> = createReducer(
  cpqOrdersInitialState,

  on(OrdersActions.SuccessGetCpqOrdersUnderReviewDataAction, (state: ICpqOrdersState, {cpqOrders}) => {
    const paginationUnderReviewOrders: IPaginationCurrent = GlueUtils.parsePaginationLinks(cpqOrders.links);
    const ordersUnderReviewPerPage: ICpqOrders[] = new Array(...state.ordersUnderReviewPerPage);
    ordersUnderReviewPerPage[paginationUnderReviewOrders.currentPage] = {
      cpqOrders: cpqOrders?.data || [],
    };

    return {
      ...state,
      ordersUnderReviewPerPage,
      paginationUnderReviewOrders,
    };
  }),

  on(OrdersActions.SuccessGetCpqOrdersFulfilledDataAction, (state: ICpqOrdersState, {cpqOrders}) => {
    const paginationFulfilledOrders: IPaginationCurrent = GlueUtils.parsePaginationLinks(cpqOrders.links);
    const ordersFulfilledPerPage: ICpqOrders[] = new Array(...state.ordersFulfilledPerPage);
    ordersFulfilledPerPage[paginationFulfilledOrders.currentPage] = {
      cpqOrders: cpqOrders?.data || [],
    };

    return {
      ...state,
      ordersFulfilledPerPage,
      paginationFulfilledOrders,
    };
  }),
);

export function CpqOrdersReducer(state: ICpqOrdersState | undefined, action: Action): ICpqOrdersState {
  return cpqOrdersReducer(state, action);
}


const ordersCartsReducer: ActionReducer<IOrdersCartsState> = createReducer(
  initialOrdersCartsState,

  on(OrdersActions.SuccessGetOrdersWithCartsDataAction, (state: IOrdersCartsState, {carts}) => {
    const canceledDeclinedCarts: IPendingOrder[] = OrdersUtils.filterCanceledDeclinedCarts(carts?.data as IPendingOrder[]);
    const canceledDeclinedOrders: IOrderHistory[] = OrdersUtils.convertDeclinedCanceledOrdersToOrderHistoryObject(canceledDeclinedCarts);
    const pendingCarts: IPendingOrder[] = OrdersUtils.filterPendingCarts(carts?.data as IPendingOrder[]);
    return {
      ...state,
      canceledDeclinedCarts: canceledDeclinedOrders,  //array of canceled/declined carts
      pendingCarts: pendingCarts,   //array of carts with status waiting
    };
  }),

  on(OrdersActions.SuccessGetCartOrdersWithApprovalDataAction, (state: IOrdersCartsState, {carts}) => {
    const canceledDeclinedCarts: IPendingOrder[] = OrdersUtils.filterCanceledDeclinedCarts(carts?.data as IPendingOrder[]);
    const canceledDeclinedOrders: IOrderHistory[] = OrdersUtils.convertDeclinedCanceledOrdersToOrderHistoryObject(canceledDeclinedCarts);
    const pendingCarts: IPendingOrder[] = OrdersUtils.filterPendingCarts(carts?.data as IPendingOrder[]);
    return {
      ...state,
      canceledDeclinedCarts: canceledDeclinedOrders,  //array of canceled/declined carts
      pendingCarts: pendingCarts,   //array of carts with status waiting
    };
  }),

  on(OrdersActions.SuccessGetOrdersHistoryDataAction, (state: IOrdersCartsState, {ordersHistory}) => {
    const ordersHistoryPagination: IPaginationCurrent = GlueUtils.parsePaginationLinks(ordersHistory?.links);
    const ordersHistoryPerPage: {ordersHistory: IOrderData[] | undefined}[] = new Array(...state.ordersHistoryPerPage);
    ordersHistoryPerPage[ordersHistoryPagination.currentPage] = {
      ordersHistory: ordersHistory?.data,
    };

    return {
      ...state,
      ordersHistoryPerPage,
      ordersHistoryPagination,
    };
  }),

  on(OrdersActions.OrdersHistoryClearDataAction, (state): IOrdersCartsState => {
    const ordersHistoryPerPage: IOrdersHistory[] = [];
    const ordersHistoryPagination: IPaginationCurrent = {...initialPagination};
    return {
      ...state,
      ordersHistoryPerPage,
      ordersHistoryPagination,
    };
  }),
);

export function OrdersCartReducer(state: IOrdersCartsState | undefined, action: Action): IOrdersCartsState {
  return ordersCartsReducer(state, action);
}

/**
 * Utility function to calculate the start offsets of virtual "pages" of the merged array
 * of canceled/declined carts and loaded sales orders as returned by {@see mergeOrdersAndCanceledDeclinedCarts()}.
 *
 * The reason these offsets need to be calculated is that the salesOrdersPerPage are "sparse"
 * due to the ability of users to skip pages instead of sequentially loading them, so for e.g.
 * a limit of 20 the start offsets into the merged list might be
 *
 * [0 => undefined, 1 => orders, 2 => orders, 3 => orders] // upto 60 orders + carts (out of at most 60) loaded
 * => [0 => 0, 1 => 0, 2 => 20, 3 => 40]
 *
 * [0 => undefined, 1=> orders, 2=> undefined, 3 => orders] // upto 40 orders + carts (out of at most 60) loaded
 * => [0 => 0, 1 => 0, 2 => 20, 3 => 20]
 *
 * @param {IOrdersHistory[]} salesOrdersPerPage
 * @param {IPaginationCurrent} ordersPagination
 * @returns {number[]}
 */
function recalculateMergedPageOffsets(
  salesOrdersPerPage: IOrdersHistory[],
  ordersPagination: IPaginationCurrent,
): number[] {
  const pageLimit: number = ordersPagination.limitPerPage;
  const numPages: number = ordersPagination.lastPage;
  let nextStartOffset: number = 0;
  const offsets: number[] = [...new Array(numPages).keys()]
    .map((value) => value + 1)
    .map((pageNum) => {
      const currentOffset: number = nextStartOffset;
      if (salesOrdersPerPage[pageNum]) {
        nextStartOffset += pageLimit;
      }
      return currentOffset;
    });
  offsets.unshift(0);
  return offsets;
}

/**
 * Utility function to merge canceled/declined carts and loaded sales orders
 * into a single flat list sorted by the proper date value as displayed in
 * the orders table.
 *
 * @param {IOrdersHistory[]} salesOrdersPerPage
 * @param {IOrderHistory[]} canceledDeclinedCarts
 * @returns {IOrderHistory[]}
 */
function mergeOrdersAndCanceledDeclinedCarts(
  salesOrdersPerPage: IOrdersHistory[],
  canceledDeclinedCarts: IOrderHistory[],
): IOrderHistory[] {
  const loadedSalesOrders: IOrderData[] = salesOrdersPerPage
      .filter(ordersPerPage => !!ordersPerPage.ordersHistory)
      .flatMap(ordersPerPage => ordersPerPage.ordersHistory || []);

  return (loadedSalesOrders as (IOrderData | IOrderHistory)[])
    .concat(canceledDeclinedCarts)
    .map((order) => {
      // temporarily extend sales orders and carts with correct date field for sorting
      const displayDate: string = order.type === EGlueResource.CARTS
        ? (order as IOrderHistory).attributes.orderedAt
        : order.attributes.createdAt;
      return {displayDate, ...order};
    }).sort((a, b) => new Date(b.displayDate).getTime() - new Date(a.displayDate).getTime(), // sort by date
    ).map(({ displayDate, ...order }) => { // NOSONAR: displayDate is intentionally destructured to strip the temporary sort field
      // remove temporary date field
      return order as IOrderHistory;
    });
}
